import { z } from "zod"

import {
  EINHEIT_ORDER,
  IVFP_PHASE_ORDER,
  einheitDefinition,
  phasenDerEinheit,
} from "./domain"
import {
  trainingsEinheitSchema,
  type GespraechsPhase,
  type TrainingsEinheit,
} from "./schemas"

export const einheitFreigabeStatusSchema = z.enum([
  "offen",
  "gesperrt",
  "geplant",
])
export type EinheitFreigabeStatus = z.infer<typeof einheitFreigabeStatusSchema>

/** Whether a state comes from the tenant or from an override of the user's group. */
export const freigabeQuelleSchema = z.enum(["mandant", "gruppe"])
export type FreigabeQuelle = z.infer<typeof freigabeQuelleSchema>

export const einheitFreigabeSchema = z.object({
  einheit: trainingsEinheitSchema,
  status: einheitFreigabeStatusSchema,
  /** ISO timestamp, only for `geplant`. */
  freigabeAb: z.string().optional(),
  quelle: freigabeQuelleSchema,
})
export const einheitFreigabeListSchema = z.array(einheitFreigabeSchema)
export type EinheitFreigabe = z.infer<typeof einheitFreigabeSchema>

// A flat object rather than a discriminated union: `createZodDto` can only
// turn object schemas into a DTO class.
export const setEinheitFreigabeSchema = z
  .object({
    status: einheitFreigabeStatusSchema,
    /** Required for `geplant`, ignored otherwise. */
    freigabeAb: z.iso.datetime({ offset: true }).optional(),
  })
  .refine((v) => v.status !== "geplant" || v.freigabeAb, {
    message: "Für eine geplante Freischaltung fehlt der Termin.",
    path: ["freigabeAb"],
  })
export type SetEinheitFreigabeInput = z.infer<typeof setEinheitFreigabeSchema>

/** Units and whole areas are released with the same body. */
export const setFreigabeSchema = setEinheitFreigabeSchema
export type SetFreigabeInput = SetEinheitFreigabeInput

/** The areas of the app that can be locked as a whole, per tenant. */
export const appBereichSchema = z.enum([
  "gespraechsauftakt",
  "gespraechstraining",
  "produkttraining",
  "pruefungsvorbereitung",
])
export type AppBereich = z.infer<typeof appBereichSchema>

export const APP_BEREICH_LABELS: Record<AppBereich, string> = {
  gespraechsauftakt: "Terminvorbereitung",
  gespraechstraining: "Gesprächstraining",
  produkttraining: "Produkttraining",
  pruefungsvorbereitung: "Prüfungsvorbereitung",
}

/**
 * The label with its article, for sentences. The areas differ in gender
 * ("das Gesprächstraining", "die Prüfungsvorbereitung"), so a fixed
 * "Das ${label}" reads wrong.
 */
export const APP_BEREICH_MIT_ARTIKEL: Record<AppBereich, string> = {
  gespraechsauftakt: "die Terminvorbereitung",
  gespraechstraining: "das Gesprächstraining",
  produkttraining: "das Produkttraining",
  pruefungsvorbereitung: "die Prüfungsvorbereitung",
}

/** Accusative, for "Deine Sparkasse hat … freigeschaltet". */
export const APP_BEREICH_AKKUSATIV: Record<AppBereich, string> = {
  gespraechsauftakt: "die Terminvorbereitung",
  gespraechstraining: "das Gesprächstraining",
  produkttraining: "das Produkttraining",
  pruefungsvorbereitung: "die Prüfungsvorbereitung",
}

/** Same, capitalised for the start of a sentence. */
export function bereichSatzanfang(bereich: AppBereich): string {
  const text = APP_BEREICH_MIT_ARTIKEL[bereich]
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const bereichFreigabeSchema = z.object({
  bereich: appBereichSchema,
  status: einheitFreigabeStatusSchema,
  /** ISO timestamp, only for `geplant`. */
  freigabeAb: z.string().optional(),
  quelle: freigabeQuelleSchema,
})
export const bereichFreigabeListSchema = z.array(bereichFreigabeSchema)
export type BereichFreigabe = z.infer<typeof bereichFreigabeSchema>

type Lock = { freigabeAb: Date | string | null } | undefined

/**
 * The effective state of a stored lock.
 *
 * Evaluated against `now` on every read instead of flipped by a job: a
 * scheduled release is just a timestamp, so there is nothing to run, and
 * nothing to miss after a restart.
 *
 * `lock` is the stored row: undefined means no row (open), `freigabeAb: null`
 * means locked until further notice.
 */
export function freigabeStatus(
  lock: Lock,
  now: Date,
): { status: EinheitFreigabeStatus; freigabeAb?: string } {
  if (!lock) return { status: "offen" }
  if (lock.freigabeAb === null) return { status: "gesperrt" }
  const ab = new Date(lock.freigabeAb)
  if (ab.getTime() <= now.getTime()) return { status: "offen" }
  return { status: "geplant", freigabeAb: ab.toISOString() }
}

/**
 * The state of one entry with a group override on top of the tenant.
 *
 * An override row has the same shape as a tenant lock, so `freigabeStatus`
 * reads both: `null` locks, a date opens from then on. Opening "now" is
 * stored as the current time, which is why a group can open what the tenant
 * keeps locked, not only lock more.
 */
function mitGruppe(
  mandant: Lock,
  gruppe: Lock,
  now: Date,
): { status: EinheitFreigabeStatus; freigabeAb?: string; quelle: FreigabeQuelle } {
  return gruppe
    ? { ...freigabeStatus(gruppe, now), quelle: "gruppe" }
    : { ...freigabeStatus(mandant, now), quelle: "mandant" }
}

type BereichLock = { bereich: AppBereich; freigabeAb: Date | string | null }

/** All areas, from the tenant's locks and, if the user is in an active group, its overrides. */
export function bereichStatusListe(
  locks: readonly BereichLock[],
  now: Date,
  gruppe: readonly BereichLock[] = [],
): BereichFreigabe[] {
  return appBereichSchema.options.map((bereich) => ({
    bereich,
    ...mitGruppe(
      locks.find((l) => l.bereich === bereich),
      gruppe.find((l) => l.bereich === bereich),
      now,
    ),
  }))
}

/** The effective state of one unit for one tenant and, optionally, one group. */
export function einheitStatus(
  einheit: TrainingsEinheit,
  lock: Lock,
  now: Date,
  gruppe?: Lock,
): EinheitFreigabe {
  // Units without content stay locked for everyone, whatever tenant or group say.
  if (!einheitDefinition(einheit).verfuegbar) {
    return { einheit, status: "gesperrt", quelle: "mandant" }
  }
  return { einheit, ...mitGruppe(lock, gruppe, now) }
}

type EinheitLock = { einheit: TrainingsEinheit; freigabeAb: Date | string | null }

/** All units in canonical order, from the tenant's locks and the group's overrides. */
export function einheitStatusListe(
  locks: readonly EinheitLock[],
  now: Date,
  gruppe: readonly EinheitLock[] = [],
): EinheitFreigabe[] {
  return EINHEIT_ORDER.map((einheit) =>
    einheitStatus(
      einheit,
      locks.find((l) => l.einheit === einheit),
      now,
      gruppe.find((l) => l.einheit === einheit),
    ),
  )
}

/**
 * What the platform sets for a group. `wie_mandant` removes the override, so
 * the group follows the tenant again, including later tenant changes.
 */
export const teamFreigabeStatusSchema = z.enum([
  "wie_mandant",
  "offen",
  "gesperrt",
  "geplant",
])
export type TeamFreigabeStatus = z.infer<typeof teamFreigabeStatusSchema>

export const setTeamFreigabeSchema = z
  .object({
    status: teamFreigabeStatusSchema,
    /** Required for `geplant`, ignored otherwise. */
    freigabeAb: z.iso.datetime({ offset: true }).optional(),
  })
  .refine((v) => v.status !== "geplant" || v.freigabeAb, {
    message: "Für eine geplante Freischaltung fehlt der Termin.",
    path: ["freigabeAb"],
  })
export type SetTeamFreigabeInput = z.infer<typeof setTeamFreigabeSchema>

const mandantWertSchema = z.object({
  status: einheitFreigabeStatusSchema,
  freigabeAb: z.string().optional(),
})

/** One group's releases for the admin: the effective state next to the tenant's. */
export const teamFreigabenSchema = z.object({
  teamId: z.string(),
  bereiche: z.array(
    bereichFreigabeSchema.extend({ mandant: mandantWertSchema }),
  ),
  einheiten: z.array(
    einheitFreigabeSchema.extend({ mandant: mandantWertSchema }),
  ),
})
export type TeamFreigaben = z.infer<typeof teamFreigabenSchema>

/** The phases an open unit covers, for entry points that still pick by phase. */
export function offenePhasen(
  liste: readonly EinheitFreigabe[],
): GespraechsPhase[] {
  return IVFP_PHASE_ORDER.filter((phase) =>
    liste.some(
      (s) => s.status === "offen" && phasenDerEinheit(s.einheit).includes(phase),
    ),
  )
}
